import { useQueryClient } from "@tanstack/react-query";
import { Trash2, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useEventSignups } from "@/hooks/useEvents";
import { deleteSignup, updateSignupStatus, type SignupStatus } from "@/lib/events";
import { handleApiError } from "@/lib/errors";
import { toast } from "@/components/ui/sonner";

const NB_DT = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

interface AdminEventSignupsListProps {
  eventId: string;
}

const AdminEventSignupsList = ({ eventId }: AdminEventSignupsListProps) => {
  const queryClient = useQueryClient();
  const { data: signups, isLoading, error } = useEventSignups(eventId);

  async function setStatus(id: string, status: SignupStatus) {
    try {
      await updateSignupStatus(id, status);
      void queryClient.invalidateQueries({ queryKey: ["event-signups", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["event-signup-summary", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["events", "admin", "with-counts"] });
      toast.success("Status oppdatert.");
    } catch (err) {
      handleApiError(err, "Kunne ikke oppdatere status.");
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Slette denne påmeldingen? Det kan ikke angres.")) return;
    try {
      await deleteSignup(id);
      void queryClient.invalidateQueries({ queryKey: ["event-signups", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["event-signup-summary", eventId] });
      void queryClient.invalidateQueries({ queryKey: ["events", "admin", "with-counts"] });
      toast.success("Påmelding slettet.");
    } catch (err) {
      handleApiError(err, "Kunne ikke slette.");
    }
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Laster påmeldinger …</p>;
  }
  if (error) {
    return <p className="text-sm text-destructive">Kunne ikke laste påmeldinger.</p>;
  }

  const items = signups ?? [];
  const totalSeats = items
    .filter((s) => s.status !== "CANCELLED")
    .reduce((n, s) => n + s.num_attendees, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Påmeldte</h2>
        <span className="text-sm text-muted-foreground">
          {items.length} påmeldinger · {totalSeats} plasser tatt
        </span>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border bg-card p-6 text-center text-sm text-muted-foreground">
          Ingen påmeldinger ennå.
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left">
              <tr>
                <th className="px-3 py-2 font-medium text-muted-foreground">Når</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Navn</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Kontakt</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Antall</th>
                <th className="px-3 py-2 font-medium text-muted-foreground">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((s) => (
                <tr key={s.id} className="align-top">
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                    {NB_DT.format(new Date(s.created_at))}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{s.name}</div>
                    {s.message && (
                      <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{s.message}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div>
                      <a href={`mailto:${s.email}`} className="hover:underline">
                        {s.email}
                      </a>
                    </div>
                    {s.phone && (
                      <a href={`tel:${s.phone}`} className="text-xs text-muted-foreground hover:underline">
                        {s.phone}
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2">{s.num_attendees}</td>
                  <td className="px-3 py-2">
                    {s.status === "CONFIRMED" ? (
                      <Badge variant="default">Bekreftet</Badge>
                    ) : s.status === "CANCELLED" ? (
                      <Badge variant="secondary">Avbrutt</Badge>
                    ) : (
                      <Badge variant="outline">Ny</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {s.status !== "CONFIRMED" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void setStatus(s.id, "CONFIRMED")}
                          title="Bekreft"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      )}
                      {s.status !== "CANCELLED" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void setStatus(s.id, "CANCELLED")}
                          title="Avbryt"
                        >
                          <XCircle className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void setStatus(s.id, "PENDING")}
                          title="Sett tilbake til Ny"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void handleDelete(s.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Slett"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminEventSignupsList;
